// Utilidades de presentación de V360: fechas en español de México, etiquetas y tonos.

export type Semaforo = "a_tiempo" | "atencion" | "en_riesgo" | "sin_fechas";
export const COLOR_SEMAFORO: Record<Semaforo, "verde" | "ambar" | "rojo" | "gris"> = {
  a_tiempo: "verde",
  atencion: "ambar",
  en_riesgo: "rojo",
  sin_fechas: "gris",
};
export const ETIQUETA_SEMAFORO: Record<Semaforo, string> = {
  a_tiempo: "A tiempo",
  atencion: "Atención",
  en_riesgo: "En riesgo",
  sin_fechas: "Sin fechas",
};

export type EstadoTarea = "pendiente" | "en_proceso" | "terminado" | "retrasado";
export const ETIQUETA_TAREA: Record<EstadoTarea, string> = {
  pendiente: "Pendiente",
  en_proceso: "En proceso",
  terminado: "Terminada",
  retrasado: "Retrasada",
};
export const TONO_TAREA: Record<EstadoTarea, "neutro" | "azul" | "verde" | "rojo"> = {
  pendiente: "neutro",
  en_proceso: "azul",
  terminado: "verde",
  retrasado: "rojo",
};

export const ETIQUETA_ENTREGABLE: Record<string, string> = {
  pendiente: "Pendiente",
  en_proceso: "En proceso",
  entregado: "Entregado",
  validado: "Validado",
  rechazado: "Con cambios",
};

export const ETIQUETA_CAMPO: Record<string, string> = {
  objetivo: "Objetivo",
  fechaPublicacion: "Fecha de publicación",
  productos: "Producto",
  areaSolicitante: "Área solicitante",
  entregables: "Entregables",
  publico: "Público",
  presupuesto: "Presupuesto",
};

export const ETIQUETA_CANAL: Record<string, string> = {
  formulario: "Formulario",
  whatsapp: "WhatsApp",
  slack: "Slack",
  correo: "Correo",
  junta: "Junta",
  verbal: "Verbal",
  api: "API",
};

/** Tono de la pill de estado de la bandeja. */
export function tonoEstado(estado: string): "neutro" | "azul" | "verde" | "ambar" | "rojo" | "tinta" {
  switch (estado) {
    case "Incompleta":
      return "ambar";
    case "Por asignar":
    case "Por desglosar":
      return "azul";
    case "En producción":
    case "Asignada":
      return "tinta";
    case "Publicada":
      return "verde";
    default:
      return "neutro";
  }
}

const DIA_LOCAL = (f: string) => new Date(`${f}T12:00:00`);

/** "dom 4 oct" */
export function fechaCorta(f: string | null | undefined): string {
  if (!f) return "—";
  return DIA_LOCAL(f)
    .toLocaleDateString("es-MX", { weekday: "short", day: "numeric", month: "short" })
    .replace(/[.,]/g, "");
}

/** "domingo 4 de octubre de 2026" */
export function fechaLarga(f: string | null | undefined): string {
  if (!f) return "Sin fecha";
  return DIA_LOCAL(f).toLocaleDateString("es-MX", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

/** "4 oct" */
export function diaMes(f: string): string {
  return DIA_LOCAL(f).toLocaleDateString("es-MX", { day: "numeric", month: "short" }).replace(".", "");
}

export function diasEntre(desde: string, hasta: string): number {
  const a = Date.UTC(+desde.slice(0, 4), +desde.slice(5, 7) - 1, +desde.slice(8, 10));
  const b = Date.UTC(+hasta.slice(0, 4), +hasta.slice(5, 7) - 1, +hasta.slice(8, 10));
  return Math.round((b - a) / 86_400_000);
}

/** "en 26 días" · "hoy" · "hace 2 días" */
export function relativo(hoy: string, f: string | null | undefined): string {
  if (!f) return "por confirmar";
  const d = diasEntre(hoy, f);
  if (d === 0) return "hoy";
  if (d === 1) return "mañana";
  if (d === -1) return "ayer";
  if (d > 0) return `en ${d} días`;
  return `hace ${-d} días`;
}

/** "hoy 8 sep · 07:00" para el historial. */
export function fechaHora(iso: string | Date, hoy?: string): string {
  const d = typeof iso === "string" ? new Date(iso) : iso;
  const dia = d.toLocaleDateString("es-MX", { weekday: "short", day: "numeric", month: "short" }).replace(/[.,]/g, "");
  const hora = d.toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" });
  const iso10 = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return `${hoy && iso10 === hoy ? "hoy " : ""}${dia} · ${hora}`;
}

export function sumarDias(fecha: string, dias: number): string {
  const d = new Date(Date.UTC(+fecha.slice(0, 4), +fecha.slice(5, 7) - 1, +fecha.slice(8, 10)));
  d.setUTCDate(d.getUTCDate() + dias);
  return d.toISOString().slice(0, 10);
}

/** Estados de solicitud, campaña y entregable como los ve Mercadotecnia (los de tarea están en ETIQUETA_TAREA). */
const ETIQUETA_ESTADO_HISTORIAL: Record<string, string> = {
  nueva: "Por asignar",
  incompleta: "Incompleta",
  asignada: "Asignada",
  en_revision: "En revisión",
  entregada: "Entregada",
  cerrada: "Cerrada",
  cancelada: "Cancelada",
  planeada: "Por desglosar",
  en_riesgo: "En riesgo",
  publicada: "Publicada",
  entregado: "Entregado",
  validado: "Validado",
  rechazado: "Rechazado",
};

/** Texto legible para un valor del historial (jsonb): estados, fechas y nombres, nunca claves del sistema. */
export function valorLegible(v: unknown): string {
  if (v == null) return "—";
  if (typeof v === "string") {
    if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return fechaCorta(v);
    if (/^\d{4}-\d{2}-\d{2}T/.test(v)) return fechaHora(v);
    return ETIQUETA_TAREA[v as EstadoTarea] ?? ETIQUETA_ESTADO_HISTORIAL[v] ?? v;
  }
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  if (Array.isArray(v)) return v.map(valorLegible).join(", ");
  if (typeof v === "object") {
    const o = v as Record<string, unknown>;
    if ("creadas" in o && Array.isArray(o.creadas)) return `${o.creadas.length} tareas: ${o.creadas.join(", ")}`;
    if ("nombre" in o && typeof o.nombre === "string") return o.nombre;
    if ("titulo" in o && typeof o.titulo === "string") return o.titulo as string;
    if ("faltan" in o && Array.isArray(o.faltan))
      return o.faltan.length > 0 ? `Incompleta; falta ${o.faltan.map((c) => ETIQUETA_CAMPO[String(c)] ?? c).join(", ")}` : "Completa";
    return Object.entries(o)
      .map(([k, x]) => `${k}: ${valorLegible(x)}`)
      .join(" · ");
  }
  return String(v);
}

export const ETIQUETA_CAMPO_HISTORIAL: Record<string, string> = {
  creada: "Se creó",
  creado: "Se creó",
  estado: "Estado",
  fechaLimite: "Fecha límite",
  fechaPublicacion: "Fecha de publicación",
  titulo: "Título",
  nombre: "Nombre",
  objetivo: "Objetivo",
  publico: "Público",
  productos: "Productos",
  areaSolicitanteId: "Área solicitante",
  solicitanteId: "Solicitante",
  presupuesto: "Presupuesto",
  prioridad: "Prioridad",
  entregables: "Entregables",
  tareas: "Tareas",
  dependencia: "Depende de",
  material_recibido: "Material recibido",
  material_validado: "Material validado",
  responsableId: "Responsable",
  areaResponsableId: "Área responsable",
  urlDrive: "Liga del material",
  descripcion: "Descripción",
  escalada_a: "Escalada a",
  canales: "Canales",
};

export function etiquetaCampo(campo: string): string {
  if (campo.startsWith("aprobacion:")) return `Aprobación de ${campo.slice(11)}`;
  return ETIQUETA_CAMPO_HISTORIAL[campo] ?? campo;
}
